import { isFunction } from "@cd2/components/build/es6"
import { assert } from "@cd2/tools"
import { useState, useCallback, useRef } from "react"

type ArrayElemenmt<T> = T extends (infer U)[] ? U : never

export type Mutator<T> = {
  get(): T
  set(value: T): void
  setPartial<K extends keyof T>(partial: Pick<T, K>): void
  toggle(value: ArrayElemenmt<T>): void
  push(value: ArrayElemenmt<T>): void
  pushIfNeeded(values: ArrayElemenmt<T>, keyExtractor?: (el: ArrayElemenmt<T>) => string): void
  pushManyIfNeeded(
    values: ArrayElemenmt<T>[],
    keyExtractor?: (el: ArrayElemenmt<T>) => string,
  ): void
  remove(values: ArrayElemenmt<T>[]): void
  filter(cb: (el: ArrayElemenmt<T>) => boolean): void
  setProp<K extends keyof T>(prop: K, value: T[K]): void
  setFn(updater: (old: T) => T): void
  setter<K extends keyof T>(prop: K): (value: T[K]) => void
  dive<K extends keyof T>(prop: K): Mutator<T[K]>
  diveBy(fn: (el: ArrayElemenmt<T>) => boolean): Mutator<ArrayElemenmt<T>>
  assertIs<T>(): Mutator<T>
}

const UNSET = Symbol("UNSET")

function buildMutator<T>(getter: () => T, setter: (value: T) => void): Mutator<T> {
  return {
    get: getter,
    set: (value: T) => setter(value),
    assertIs<U>(this: Mutator<T>) {
      return this as any as Mutator<U>
    },
    setFn(updater: (old: T) => T) {
      setter(updater(getter()))
    },
    setPartial<K extends keyof T>(partial: Pick<T, K>) {
      const value = getter()
      assert(typeof value === "object" && value !== null, "setPartial can only be used on objects")
      setter({ ...value, ...partial } as T)
    },
    toggle(value: ArrayElemenmt<T>) {
      const array = getter()
      assert(Array.isArray(array), "toggle can only be used on arrays")

      if (array.includes(value)) {
        setter(array.filter(el => el !== value) as T)
      } else {
        setter([...array, value] as T)
      }
    },
    push(value: ArrayElemenmt<T>) {
      const array = getter()
      assert(Array.isArray(array), "push can only be used on arrays")

      setter([...array, value] as T)
    },
    pushIfNeeded(value: ArrayElemenmt<T>, keyExtractor?: (el: ArrayElemenmt<T>) => string) {
      const array = getter()
      assert(Array.isArray(array), "pushIfNeeded can only be used on arrays")

      const keys = array.map(el => (keyExtractor ? keyExtractor(el) : el))

      if (!keys.includes(keyExtractor ? keyExtractor(value) : value)) {
        setter([...array, value] as T)
      }
    },
    pushManyIfNeeded(values: ArrayElemenmt<T>[], keyExtractor?: (el: ArrayElemenmt<T>) => string) {
      const array = getter()
      assert(Array.isArray(array), "pushManyIfNeeded can only be used on arrays")

      const keys = array.map(el => (keyExtractor ? keyExtractor(el) : el))

      const newArray = [...array] as ArrayElemenmt<T>[]
      let changed = false
      values.forEach(value => {
        const key = keyExtractor ? keyExtractor(value) : value
        if (!keys.includes(key)) {
          changed = true
          newArray.push(value)
        }
      })

      if (changed) setter(newArray as T)
    },
    remove(values: ArrayElemenmt<T>[]) {
      const array = getter()
      assert(Array.isArray(array), "pushIfNeeded can only be used on arrays")

      setter(array.filter(el => !values.includes(el)) as T)
    },
    filter(cb: (el: ArrayElemenmt<T>) => boolean) {
      const array = getter()
      assert(Array.isArray(array), "pushIfNeeded can only be used on arrays")

      setter(array.filter(cb) as T)
    },
    setProp<K extends keyof T>(prop: K, value: T[K]) {
      const obj = getter()
      assert(typeof obj === "object" && obj !== null, "setProp can only be used on objects")

      setter({ ...obj, [prop]: value } as T)
    },
    setter<K extends keyof T>(prop: K) {
      return (value: T[K]) => {
        const obj = getter()
        assert(typeof obj === "object" && obj !== null, "setter can only be used on objects")

        setter({ ...obj, [prop]: value } as T)
      }
    },
    dive<K extends keyof T>(prop: K) {
      const obj = getter()
      assert(typeof obj === "object" && obj !== null, "dive can only be used on objects")
      if (Array.isArray(obj)) {
        assert(typeof prop === "number", "dive can only be used with a number on arrays")
      }
      return buildMutator(
        () => getter()[prop],
        (value: T[K]) => {
          const current = getter()
          if (Array.isArray(current)) {
            current[prop as number] = value
            setter([...current] as T)
          } else {
            setter({ ...current, [prop]: value } as T)
          }
        },
      )
    },
    diveBy(fn: (el: ArrayElemenmt<T>) => boolean) {
      const array = getter()
      assert(Array.isArray(array), "diveBy can only be used on arrays")

      return buildMutator(
        () => {
          const array = getter()
          assert(Array.isArray(array), "diveBy can only be used on arrays")
          const index = array.findIndex(fn)
          assert(index !== -1, "diveBy did not find a matching element")
          return array[index]
        },
        (value: ArrayElemenmt<T>) => {
          const array = getter()
          assert(Array.isArray(array), "diveBy can only be used on arrays")
          const index = array.findIndex(fn)
          assert(index !== -1, "diveBy did not find a matching element")
          setter([...array.slice(0, index), value, ...array.slice(index + 1)] as T)
        },
      )
    },
  }
}

export function useMutatorState<T>(initialState: T | (() => T)): [T, Mutator<T>] {
  const [_, setUpdateKey] = useState(0)
  const triggerUpdate = useCallback(() => setUpdateKey(prev => prev + 1), [])

  const stateRef = useRef<T>(
    // @ts-ignore This will be updated on the next line
    UNSET,
  )
  if (stateRef.current === UNSET) {
    stateRef.current = isFunction(initialState) ? initialState() : initialState
  }
  const setState = useCallback(
    (newState: unknown) => {
      const newStateValue =
        typeof newState === "function" ? (newState as any)(stateRef.current) : newState
      stateRef.current = newStateValue
      triggerUpdate()
    },
    [triggerUpdate],
  )

  const mutate = buildMutator<T>(() => stateRef.current, setState)
  return [stateRef.current, mutate]
}
